import { faArrowsRotate, faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import capitalize from 'lodash.capitalize';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { OnboardingTask } from '@feathr/blackbox';
import { EOnboardingMilestone } from '@feathr/blackbox';
import {
  Button,
  CardV2 as Card,
  Checkbox,
  Icon,
  List,
  showError,
  toast,
  Tooltip,
  Well,
} from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';
import { isWretchError } from '@feathr/rachis';
import { useAccount, useFlags, useRole } from '@feathr/report_components';

import { getMilestoneTasks } from '../../OnboardingPage/MilestonePage/MilestoneTasks';
import { handleLogOut } from '../utils';
import { renderAccountSetupTasks, renderEmailCampaignTasks } from './FeathrLiteOnboarding.utils';

interface IProps {
  setIsResetting: (value: boolean) => void;
  isResetting: boolean;
}

function FeathrLiteOnboardingCard({ setIsResetting, isResetting }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { OnboardingTasks } = useStore();
  const { blackboxVersion } = useBlackboxVersion();
  const { showOnboarding, enforceOnboarding } = useFlags();
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const [selectedMilestones, setSelectedMilestones] = useState<EOnboardingMilestone[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<OnboardingTask[]>([]);
  const [selectedTaskRequiresAccountSetup, setSelectedTaskRequiresAccountSetup] = useState(false);

  const { isGlobal } = useRole();

  useEffect(() => {
    async function init(): Promise<void> {
      const tasks = OnboardingTasks.list();
      await when(() => tasks.isLoaded);
      setTasks(tasks.models);
    }

    init();
  }, []);

  async function handleFactoryReset(): Promise<void> {
    setIsResetting(true);
    toast(t('Account reset in progress. Once complete, you will be logged out.'), {
      type: 'info',
    });

    try {
      const response = await account.resetOnboarding();
      if (isWretchError(response)) {
        throw response.error;
      }

      toast(t('Account reset successfully'), {
        type: 'success',
      });

      await handleLogOut(t);
    } catch (error) {
      showError(error, t);
    }
  }

  async function handleSelectiveReset(): Promise<void> {
    setIsResetting(true);
    try {
      toast(t('Account reset in progress. Once complete, you will be logged out.'), {
        type: 'info',
      });
      const response = await account.resetOnboarding({
        milestones: selectedMilestones,
        tasks: selectedTasks.map((t) => t.get('_cls')),
      });

      if (isWretchError(response)) {
        throw response.error;
      }

      toast(t('Account reset successfully'), {
        type: 'success',
      });

      await handleLogOut(t);
    } catch (error) {
      showError(
        t('There was an error resetting the account./n:{{- error}}', {
          error: error instanceof Error ? error.message : error,
        }),
        t,
      );
    }
  }

  function handleSelectInitialAccountSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.InitialAccountSetup]
        : [...prev.filter((m) => m !== EOnboardingMilestone.InitialAccountSetup)],
    );

    setSelectedTasks((prev) =>
      prev.filter((t) => t.get('milestone') !== EOnboardingMilestone.InitialAccountSetup),
    );
  }

  function handleSelectAdCampaignSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.OmniCampaign]
        : [...prev.filter((m) => m !== EOnboardingMilestone.OmniCampaign)],
    );

    handleSelectInitialAccountSetup(true);
  }

  function handleSelectEmailCampaignSetup(value?: boolean): void {
    setSelectedMilestones((prev) =>
      value
        ? [...prev, EOnboardingMilestone.SingleSendCampaign]
        : [...prev.filter((m) => m !== EOnboardingMilestone.SingleSendCampaign)],
    );

    setSelectedTasks((prev) =>
      prev.filter((t) => t.get('milestone') !== EOnboardingMilestone.SingleSendCampaign),
    );

    handleSelectInitialAccountSetup(true);
  }

  function handleSelectTask(task: OnboardingTask): void {
    const requiresInitialAccountSetup = (task: OnboardingTask): boolean => {
      return [EOnboardingMilestone.OmniCampaign, EOnboardingMilestone.SingleSendCampaign].includes(
        task.get('milestone'),
      );
    };

    if (requiresInitialAccountSetup(task)) {
      setSelectedTaskRequiresAccountSetup(!selectedTasks.includes(task));
      handleSelectInitialAccountSetup(!selectedTasks.includes(task));
    }

    setSelectedTasks((prev) =>
      prev.includes(task) ? [...prev.filter((t) => t !== task)] : [...prev, task],
    );
  }

  const accountMilestones = getMilestoneTasks(EOnboardingMilestone.InitialAccountSetup, t);
  const emailMilestones = getMilestoneTasks(EOnboardingMilestone.SingleSendCampaign, t);

  const selectiveResetTooltip = (function (): string | undefined {
    if (!isGlobal) {
      return t('Global role required');
    }

    if (!selectedMilestones.length && !selectedTasks.length) {
      return t('No milestones or tasks selected');
    }

    return undefined;
  })();

  const initialAccountSetupCheckbox = (): ReactNode => {
    const checkbox = (
      <Checkbox
        disabled={
          !isGlobal ||
          selectedMilestones.some((m) =>
            [EOnboardingMilestone.OmniCampaign, EOnboardingMilestone.SingleSendCampaign].includes(
              m,
            ),
          ) ||
          selectedTaskRequiresAccountSetup ||
          isResetting
        }
        label={t('Initial Account Setup')}
        onChange={handleSelectInitialAccountSetup}
        value={selectedMilestones.includes(EOnboardingMilestone.InitialAccountSetup)}
      />
    );

    return selectedTaskRequiresAccountSetup ||
      selectedMilestones.some((m) =>
        [EOnboardingMilestone.OmniCampaign, EOnboardingMilestone.SingleSendCampaign].includes(m),
      ) ? (
      <Tooltip title={t('A selected milestone or task requires this milestone to be completed.')}>
        {checkbox}
      </Tooltip>
    ) : (
      checkbox
    );
  };

  return (
    <>
      <Card disabled={isResetting}>
        <Card.Header title={t('Feathr Platform - Lite - Onboarding')} />
        <Card.Content action={capitalize(blackboxVersion?.DB_ENV)} title={t('Environment')} />
        <Card.Content addVerticalGap={true} title={t('Flags')}>
          <Well label={t('Show Onboarding')}>
            <Icon
              color={showOnboarding ? 'green' : 'red'}
              icon={showOnboarding ? faCheck : faXmark}
            />
          </Well>
          <Well label={t('Onboarding Enforced')}>
            <Icon
              color={enforceOnboarding ? 'green' : 'red'}
              icon={enforceOnboarding ? faCheck : faXmark}
            />
          </Well>
        </Card.Content>
      </Card>

      <Card disabled={isResetting}>
        <Card.Header
          description={t('Fully purge the account and reset as a clean slate.')}
          isCollapsible={true}
          title={t('Factory Reset')}
        />
        <Card.Content>
          <Stack>
            <Trans t={t}>
              <List hasBullets={true}>
                <List.Item>All milestones and tasks will be reset to "Not started."</List.Item>
                <List.Item>The payment method for the account will be deleted.</List.Item>
                <List.Item>
                  ALL DATA associated with the account will be deleted. Flights, Events, Campaigns,
                  Groups, etc will all be deleted.
                </List.Item>
                <List.Item>
                  Current users (excluding those with global roles) will not have access to the
                  account and must be re-invited.
                </List.Item>
              </List>
            </Trans>
          </Stack>
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={!isGlobal || isResetting}
            onClick={handleFactoryReset}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={
              !isGlobal
                ? t('Global role required')
                : isResetting
                  ? t('Reset in progress')
                  : undefined
            }
            type={'danger'}
          >
            {t('Reset')}
          </Button>
        </Card.Actions>
      </Card>
      <Card disabled={!isGlobal || isResetting}>
        <Card.Header
          description={t(
            'Reset the account with certain milestones and/or tasks already completed.',
          )}
          isCollapsedDefault={true}
          isCollapsible={true}
          title={t('Selective Reset')}
        />
        <Card.Content addVerticalGap={true}>
          <Well label={initialAccountSetupCheckbox()} layout={'vertical'}>
            {renderAccountSetupTasks({
              tasks,
              selectedMilestones,
              selectedTasks,
              isGlobal,
              isResetting,
              accountMilestones,
              handleSelectTask,
            })}
          </Well>
          <Checkbox
            disabled={!isGlobal || isResetting}
            label={t('Ad Campaign Setup')}
            layout={'well'}
            onChange={handleSelectAdCampaignSetup}
            value={selectedMilestones.includes(EOnboardingMilestone.OmniCampaign)}
          />

          <Well
            label={
              <Checkbox
                disabled={!isGlobal || isResetting}
                label={t('Email Campaign Setup')}
                onChange={handleSelectEmailCampaignSetup}
                value={selectedMilestones.includes(EOnboardingMilestone.SingleSendCampaign)}
              />
            }
            layout={'vertical'}
          >
            {renderEmailCampaignTasks({
              tasks,
              selectedMilestones,
              selectedTasks,
              isGlobal,
              isResetting,
              emailMilestones,
              handleSelectTask,
            })}
          </Well>
        </Card.Content>
        <Card.Actions>
          <Button
            disabled={
              (!selectedMilestones.length && !selectedTasks.length) || !isGlobal || isResetting
            }
            onClick={handleSelectiveReset}
            prefix={<Icon icon={faArrowsRotate} />}
            tooltip={selectiveResetTooltip}
            type={'danger'}
          >
            {t('Reset')}
          </Button>
        </Card.Actions>
      </Card>
    </>
  );
}

export default observer(FeathrLiteOnboardingCard);
